// Pure helpers behind the research pane: what a catalogue variant's citations verified to, whether it
// may be shown, and what may be posted to the attach verb.
import { CatalogueVariant } from "../../../../types/geneCodes/VariantCatalogueTypes";
import { Staging } from "../../../../types/geneCodes/StagingTypes";
import { toAttachPayload } from "./snpCatalogue";

export interface ResearchCandidate {
	variant: CatalogueVariant;
	verified: string[];
	unverified: string[];
	// Only ever true while the literature half answered: a citation we could not check is not an unverified one.
	hidden: boolean;
	// membership.panels minus the wheel we are on — `in_current_panel` already carries that, so listing it twice double-counts.
	otherPanels: string[];
}

/**
 * `verifiedPmids === null` means the literature half is unavailable: nothing is verified, and so
 * nothing may be hidden either. Filtering on an unanswered question is how "we could not check"
 * silently becomes "there is nothing".
 */
export const buildResearchCandidates = (variants: CatalogueVariant[], verifiedPmids: string[] | null, currentPanel: string | null): ResearchCandidate[] => {
	const corpus = verifiedPmids === null ? null : new Set(verifiedPmids);

	return variants.map((variant) => {
		const verified = corpus === null ? [] : variant.pmids.filter((pmid) => corpus.has(pmid));
		const unverified = corpus === null ? [] : variant.pmids.filter((pmid) => !corpus.has(pmid));

		return {
			variant,
			verified,
			unverified,
			hidden: corpus !== null && verified.length === 0,
			otherPanels: variant.membership.panels.filter((name) => name !== currentPanel),
		};
	});
};

/** The EFO and MeSH terms the returned rows resolved to, deduped in first-seen order. */
export const resolvedTerms = (variants: CatalogueVariant[], ontology: "efo" | "mesh"): string[] => {
	const seen: string[] = [];
	variants.forEach((variant) => variant.terms[ontology].forEach((term) => {
		if (term !== "" && !seen.includes(term)) seen.push(term);
	}));
	return seen;
};

/** The attach payload for one variant. ATTACH_KEYS covers rs/gene/risk_allele; the rest is dropped server-side. */
export const attachPayloadForVariant = (variant: CatalogueVariant, subcategory: string | null): Record<string, unknown> =>
	toAttachPayload({ rs: variant.rs, gene: variant.gene, risk_allele: variant.risk_allele } as unknown as Staging, subcategory);
